import { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { notificationsAPI } from '../services/api';

const SOCKET_SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '');

const getDeletedIds = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem('bciis_deleted_notifs') || '[]').map(String));
  } catch {
    return new Set();
  }
};

const addDeletedId = (id) => {
  try {
    const list = JSON.parse(localStorage.getItem('bciis_deleted_notifs') || '[]');
    const idStr = String(id);
    if (!list.includes(idStr)) {
      list.push(idStr);
      // Keep list trimmed to recent 500
      if (list.length > 500) list.shift();
      localStorage.setItem('bciis_deleted_notifs', JSON.stringify(list));
    }
  } catch {
    // ignore
  }
};

export function useNotifications(user) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await notificationsAPI.getNotifications(user.id || user.username, user.role);
      if (res.success) {
        const deletedIds = getDeletedIds();
        // Strictly filter out any deleted notifications
        const activeList = (res.notifications || []).filter(n => !deletedIds.has(String(n.id)));
        setNotifications(activeList);
        const unread = activeList.filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Real-time Socket.IO notification listener for instant bell badge updates
  useEffect(() => {
    if (!user) return;
    const userId = user.id || user.username;

    const socket = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socket.on('connect', () => {
      socket.emit('join', userId);
    });

    socket.on('new_notification', (notif) => {
      const deletedIds = getDeletedIds();
      if (!deletedIds.has(String(notif.id))) {
        setNotifications(prev => [notif, ...prev.filter(n => String(n.id) !== String(notif.id))]);
        setUnreadCount(prev => prev + 1);
      }
    });

    socket.on('new_message', (msg) => {
      // If user is recipient of message, trigger notification update
      const stripPrefix = id => String(id || '').replace(/^[a-z]+_/i, '');
      if (stripPrefix(msg.receiverId) === stripPrefix(userId)) {
        fetchNotifications();
      }
    });

    socket.on('notification_deleted', ({ id }) => {
      addDeletedId(id);
      setNotifications(prev => {
        const target = prev.find(n => String(n.id) === String(id));
        if (target && !target.read) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => String(n.id) !== String(id));
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [user, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId, user?.id || user?.username);
      setNotifications(prev =>
        prev.map(n => (notificationId ? n.id === notificationId : true) ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => notificationId ? Math.max(0, prev - 1) : 0);
    } catch (err) {
      // ignore
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // 1. Mark as permanently deleted in local cache
      addDeletedId(notificationId);

      // 2. Instant real-time UI removal
      setNotifications(prev => {
        const target = prev.find(n => String(n.id) === String(notificationId));
        if (target && !target.read) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => String(n.id) !== String(notificationId));
      });

      // 3. Delete from backend database
      await notificationsAPI.deleteNotification(notificationId, user?.id || user?.username);
    } catch (err) {
      console.warn('[useNotifications] Delete error:', err);
    }
  };

  const clearAll = async () => {
    try {
      // Mark all current notifications as deleted
      notifications.forEach(n => addDeletedId(n.id));
      setNotifications([]);
      setUnreadCount(0);
      await notificationsAPI.clearNotifications(user?.id || user?.username);
    } catch (err) {
      // ignore
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    deleteNotification,
    clearAll
  };
}
